import { 
  Button, 
  Drawer, 
  useMediaQuery, 
  Box, 
  Card, 
  CardContent, 
  FormHelperText, 
  Grid, 
  TextField as MuiTextField,
  CardHeader, MenuItem, TextFieldProps, Step, StepContent, StepLabel, Stepper, Typography, StepButton, FormControlLabel, Radio, Switch, Theme } from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { useState, FC, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "~/components/formik/OdForm";
import TextField from "~/components/formik/TextField";
import { ReduxState, useCreate, useGetList, useGetMainList } from "ra-core";
import SelectField from "../formik/SelectField";
import { BackupResponseDto, DatabaseResponseDto } from "~/gen/api/backup";
import { pages, sorts } from "~/utils/paramsWithGetMainList";
import { RecPatch } from "~/dataProvider/tools";
import { SaveActionSlim } from "~/dataProvider/types";
import { AnySchema } from "yup";
import { useUserTimeFormat } from "~/utils/userTimeFormat";
import RadioGroupField from "../formik/RadioGroupFields";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { initialValues, raiseStashDeployInfo, raiseUnstashDeployInfo, StashPayload } from "~/actions/deployStash";
import { getStashedDeployInfo } from "~/reducers/deployStash";
import { useEffect } from "react";
import { EncryptionResponseDto, EncryptionStatus } from "~/gen/api/encrypt";
import { CustomizedOption, NetworkConnectionResponseDto, NewDeploymentInput, VmSkuResponseDto } from "~/gen/api/deploy";
import { useLocation } from "react-router-dom";
import chainOptions from "~/utils/chainOptions";
import { useSnackbar } from "notistack";
import { genUrlWithHashid } from "~/utils/hashid";
import retrieveAxiosErr from "~/utils/retrieveAxiosErr";
import { CloudSubscriptionResponseDto } from "~/gen/api/user";
import { crudGetOneEx } from "~/actions/crudFetchEx";
import { useRevealPass } from "~/utils/useRevealPass";


const resource_deployment = 'deployments/list'

const validationSchema =
  Yup.object().shape({
    database: Yup.object().required('Database is required'),
    deployFrom: Yup.mixed().oneOf(['backup', 'encryption']).required('Deploy From is required'),
    backupOption: Yup.mixed().when('deployFrom', (deployFrom:string, schema: AnySchema) => deployFrom === 'backup' ? schema.required('Should select a Backup version') : schema),
    encryptionOption: Yup.mixed().when('deployFrom', (deployFrom:string, schema: AnySchema) => deployFrom === 'encryption' ? schema.required('Should select an Encryption version') : schema),
    hostingOption: Yup.object().required('Hosting Option is required'),
    instanceName: Yup.string().max(255)
      .matches(/^[a-zA-Z][a-zA-Z0-9_]+$/, 'Database name can only contain characters or numbers or _ and must begin with a character'),
    initialDBUser: Yup.string().max(255).required('Database Owner User Name is required'),
    initialDBPass: Yup.string().min(12).max(255).required('Password is required')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#$^+=!*()@%&]).{12,}$/, "Password must contain uppercase and lowercase characters, numbers and special characters."),
  })

const otherValidate = (showNetworkInput: boolean) => (values: typeof initialValues) => {
  const errors: any = {}
  if (values.database && !values.database.isManaged) {
    if (!values.migrateNeeded && !values.instanceName) {
      errors['instanceName'] = 'Database Name is required'
    }
  }
  if (showNetworkInput && !values.networkConnect) {
    errors['networkConnect'] = 'Network Connectivity is required'
  }
  if (Object.keys(errors).length > 0) {
    return errors
  }
}

type BackupEncryptionOption = BackupResponseDto | EncryptionResponseDto;

const fieldsArr = {
  0: [ 'database', 'deployFrom', 'backupOption', 'encryptionOption' ],
  1: [ 'networkConnect' ],
  2: [ 'deployCharater', 'performanceTier', 'hostingOption' ],
  3: [ 'initialDBUser', 'initialDBPass' ],
  4: [ 'instanceName', 'migrateNeeded', 'cloneEncryptionMapping' ],
} as Record<string, Array<keyof(typeof initialValues)>>;


const cloudSubscriptionRes = 'settings/organization/cloud-subscription'

const DeployDatabase: FC = () => {
  const [save] = useCreate(resource_deployment, undefined, { mutationMode: 'pessimistic' })
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()

  const handleSave: SaveActionSlim<any> = (rec, callbacks) => {
    save(resource_deployment, rec, chainOptions(callbacks, {
      onSuccess: ({data}: {data:RecPatch}) => {
        dispatch(push(genUrlWithHashid(`/${resource_deployment}/$0/show`, data.id, rec.databaseName || '')))
        enqueueSnackbar('Deploy database succeed', { variant: 'success' })
      },
      onFailure: (err) => {
        const errMsg = retrieveAxiosErr(err)
        enqueueSnackbar(`Deploy database failed: ${errMsg}`, { variant: 'error' })
      },
    }))
  }

  const cloudSubs = useSelector<ReduxState, CloudSubscriptionResponseDto&RecPatch | undefined>(store => store.admin.resources[cloudSubscriptionRes]?.data[1])
  useEffect(() => {
    dispatch(crudGetOneEx(cloudSubscriptionRes, 1, ''))
  }, [dispatch])

  return (
    <DeployDatabaseBody onSave={handleSave} showNetworkInput={!(cloudSubs?.isEnabled)} />
  )
}

export interface DeployDatabaseBodyProps {
  onSave: SaveActionSlim<any>;
  showNetworkInput: boolean;
}

export const DeployDatabaseBody: FC<DeployDatabaseBodyProps> = ({
  onSave,
  showNetworkInput,
}) => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const location = useLocation()
  const match = useMediaQuery((theme:Theme) => theme.breakpoints.down('md'))
  const width = match ? 'calc(100vw - 50px)' : 850

  useEffect(() => {
    if (location.state && typeof location.state === 'object') {
      const { openCreate }: {openCreate:boolean} = location.state as any
      if (openCreate) {
        setOpen(true)
        dispatch(push(location.pathname, null))
      }
    }
  }, [location, dispatch])

  const stashed: StashPayload | null = useSelector(getStashedDeployInfo)

  useEffect(() => {
    stashed && setOpen(true)
  }, [stashed])

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
    resetForm,
  }) => {
    const options = {
      onSuccess: () => {
        resetForm({
          status: { success: true },
          values: initialValues,
          isSubmitting: false,
        })
        handleClose()
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }
    const input: NewDeploymentInput = {
      databaseId: values.database !== '' ? (values.database.databaseId || -1) : -1,
      backupId: values.deployFrom === 'backup'
        ? (values.backupOption !== '' ? values.backupOption.backupId : undefined)
        : null,
      encryptionId: values.deployFrom === 'encryption'
        ? (values.encryptionOption !== '' ? values.encryptionOption.encryptionId : undefined)
        : null,
      isProduction: values.deployCharater === 'production',
      customizedOption: values.performanceTier !== '' ? +(values.performanceTier) : undefined,
      username: values.initialDBUser,
      password: values.initialDBPass,
      ifMigrate: values.database && values.database.isManaged ? false : values.migrateNeeded,
      databaseName: values.instanceName,
      ifCloneMappings: values.cloneEncryptionMapping,
    }
    onSave(input, options)
  }, [onSave])

  const formikCtrl = useFormik({ 
    initialValues: stashed ? stashed.values : initialValues, 
    onSubmit, 
    validationSchema, 
    validate: otherValidate(showNetworkInput)
  })
  const { errors, touched, isSubmitting, values, resetForm, handleChange, setFieldTouched, setTouched, setFieldValue } = formikCtrl

  const { data: databaseData } = useGetMainList<DatabaseResponseDto & RecPatch>('databases', pages(), sorts(), {})
  const databases = Object.values(databaseData || {}).sort((a, b) => (a.name || '') < (b.name || '') ? -1 : 1)

  const { data: backupOptionData } = useGetList<BackupResponseDto & RecPatch>(
    values.database === '' ? 'noop' : 'encryptable-backups', 
    pages(), sorts(), 
    { databaseId: values.database === '' ? -1 : values.database.databaseId })
  const backupOptions = Object.values(backupOptionData || {}).sort((a, b) => (a.createdTime || '') < (b.createdTime || '') ? 1 : -1)

  const { data: encryptionOptionData } = useGetList<EncryptionResponseDto & RecPatch>(
    values.database === '' ? 'noop' : 'encryption-historic-recs', 
    pages(), sorts(), 
    { databaseId: values.database === '' ? -1 : values.database.databaseId })
  const encryptionOptions = 
    Object.values(encryptionOptionData || {})
      .filter(e => e.status === EncryptionStatus.Success)
      .sort((a, b) => (a.createdTime || '') < (b.createdTime || '') ? 1 : -1)

  const { data: hostingOptionsData } = useGetList<VmSkuResponseDto & RecPatch>(
    values.database && (values.backupOption || values.encryptionOption) && values.deployCharater && values.performanceTier ? 'host-options' : 'noop',
    pages(), sorts(), 
    {
      databaseId: (values.database !== '') ? values.database.databaseId : -1, 
      ...(values.deployFrom === 'backup' && { backupId: !!values.backupOption ? values.backupOption.backupId : undefined }),
      ...(values.deployFrom === 'encryption' && { encryptionId: !!values.encryptionOption ? values.encryptionOption.encryptionId : undefined }),
      customizedOption: values.performanceTier,
      isProduction: values.deployCharater === 'production', 
    })
  const hostingOptions = Object.values(hostingOptionsData || {})

  const { data: networkConnectionData } = useGetMainList<NetworkConnectionResponseDto & RecPatch>(
    showNetworkInput ? 'settings/deployment/network-configuration' : 'noop', 
    pages(), sorts(), {})
  const networkConnections = Object.values(networkConnectionData || {})

  const handleOpen = useCallback((): void => {
    setOpen(true)
  }, [])
  const handleClose = useCallback((): void => {
    setOpen(false);
    resetForm({ 
      values: {...initialValues},
      isSubmitting: false,
    })
    handleReset()
  }, [])

  const format = useUserTimeFormat()

  const [activeStep, setActiveStep] = useState(stashed?.activeStep || 0);
  useEffect(() => {
    if (stashed) {
      setTouched(stashed.touched)
      dispatch(raiseUnstashDeployInfo())
    }
  }, [stashed])

  const offset = showNetworkInput ? 0 : -1
  const handleNext = () => {
    setActiveStep((prevActiveStep) => {
      const fieldsArrIdx = prevActiveStep === 0 ? 0 
        : showNetworkInput ? prevActiveStep
        : prevActiveStep - offset
      fieldsArr[fieldsArrIdx] && fieldsArr[fieldsArrIdx].forEach(s => setFieldTouched(s, true))
      setTimeout(() => {
        const stepBtns:HTMLElement[] = document.querySelectorAll('button.MuiStepButton-root') as any
        if (stepBtns[prevActiveStep + 1]) {
          stepBtns[prevActiveStep + 1].focus()
        }
      }, 10)
      return prevActiveStep + 1
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const handleActivate = (i:number) => () => {
    setActiveStep(i)
  }

  const singleCompleted = (s: keyof(typeof initialValues)) => touched[s] && !errors[s]
  const singleError = (s: keyof(typeof initialValues)) => touched[s] && errors[s]

  const completion = Object.keys(fieldsArr).reduce((acc, g) => {
    acc[g] = fieldsArr[g].every(singleCompleted)
    return acc
  }, {} as Record<string, boolean>)

  const stepsError = Object.keys(fieldsArr).reduce((acc, g) => {
    acc[g] = fieldsArr[g].some(singleError)
    return acc
  }, {} as Record<string, boolean>)

  const handleAddNewConnection = () => {
    // store component state into redux store
    dispatch(raiseStashDeployInfo(values, activeStep, touched))
    dispatch(push('/settings/deployment/network-configuration'))
  }

  useEffect(() => {
    const seleted = values.deployFrom === 'backup' ? values.backupOption : values.encryptionOption
    if (values.database !== '' && seleted !== '') {
      if (values.database.databaseId !== seleted.databaseId) {
        setFieldValue('backupOption', '')
        setFieldValue('encryptionOption', '')
      }
    }
  }, [values.deployFrom, values.backupOption, values.encryptionOption, values.database])

  useEffect(() => {
    setFieldValue('hostingOption', '')
  }, [values.deployCharater, values.performanceTier])

  useEffect(() => {
    if (hostingOptions.length === 1) {
      setFieldValue('hostingOption', hostingOptions[0])
    }
  }, [JSON.stringify(hostingOptions.map(h => h.id).sort().join(','))])

  useEffect(() => {
    if (values.database !== '') {
      const d0 = values.database
      const d1 = databases.find(db => db.databaseId === d0.databaseId && db !== d0)
      d1 && setFieldValue('database', d1)
    }
    if (values.backupOption !== '') {
      const b0 = values.backupOption
      const b1 = backupOptions.find(bk => bk.backupId === b0.backupId && bk !== b0)
      b1 && setFieldValue('backupOption', b1)
    }
    if (values.encryptionOption !== '') {
      const b0 = values.encryptionOption
      const b1 = encryptionOptions.find(bk => bk.encryptionId === b0.encryptionId && bk !== b0)
      b1 && setFieldValue('encryptionOption', b1)
    }
    if (showNetworkInput && values.networkConnect !== '') {
      const n0 = values.networkConnect
      const n1 = networkConnections.find(conn => conn.networkConnectionId === n0.networkConnectionId && conn !== n0)
      n1 && setFieldValue('networkConnect', n1)
    }
    if (values.hostingOption !== '') {
      const h0 = values.hostingOption
      const h1 = hostingOptions.find(ho => ho.id === (h0 as any).id && ho !== h0)
      h1 && setFieldValue('hostingOption', h1)
    }
  })

  const { showPass, endAdornment } = useRevealPass()

  return (
    <>
      <Button
        onClick={handleOpen}
        variant="contained"
      >
        Deploy Database
      </Button>
      <Drawer
        anchor="right"
        onClose={handleClose}
        open={open}
        PaperProps={{
          sx: {
            p: 2,
            width: width,
          }
        }}
      >
        
        
        <OdForm formikCtrl={formikCtrl}>

          <Card>
            <CardHeader title="Deploy Database" />
            <CardContent>
              
              <Stepper 
                activeStep={activeStep} 
                orientation="vertical" 
                nonLinear={true}
                sx={{ '& .MuiGrid-container': { mt: 0 } }}
              >
                <Step completed={completion[0]}>
                  <StepButton onClick={handleActivate(0)}>
                    <StepLabel error={stepsError[0]} >
                      {activeStep !== 0 && completion[0] 
                        ? `From "${(values.database as DatabaseResponseDto).name}"`
                        : "Select Data Source"}
                      {activeStep !== 0 && completion[0] && 
                        <>
                          <Typography variant="caption" component="p" sx={{ lineHeight: 2.8 }}>
                            {values.deployFrom === "backup" ? 'Backup ' : 'Encryption '}
                            {format(((values.deployFrom === "backup" ? values.backupOption : values.encryptionOption) as BackupEncryptionOption).createdTime || 0, 'Pp')}
                          </Typography>
                          <Typography variant="caption" component="p">
                            {(values.database as DatabaseResponseDto).engineVersion}
                            {values.database !== '' && values.database.sizeText ? ' / ' + values.database.sizeText.toUpperCase() : ''}
                          </Typography>
                        </>
                      }
                    </StepLabel>
                  </StepButton>
                  <StepContent>
                    <Grid
                      container
                      spacing={4}
                    >
                      <Grid item xs={12} md={6} >
                        <SelectField label="Database*" name="database" >
                          {databases.map(d => (
                            <MenuItem key={d.databaseId} value={d as any} >{d.name}</MenuItem>
                          ))}
                        </SelectField>
                      </Grid>
                      <Grid item xs={12} md={6} >
                        <ReadonlyField 
                          id="engine"
                          label="Engine" 
                          value={values.database === '' 
                            ? '' : `${values.database.engineVersion || ''} ${values.database.engineEdition || ''}` 
                          } 
                        />
                      </Grid>
                      <Grid item xs={12} md={6} >
                        <SelectField label="Deploy From*" name="deployFrom">
                          <MenuItem value="backup">Database Backups</MenuItem>
                          <MenuItem value="encryption">Encrypted Versions</MenuItem>
                        </SelectField>
                      </Grid>
                      <Grid item xs={12} md={6} >
                        {values.deployFrom === 'backup' ?
                          <SelectField label="Backup Timestamp*" name="backupOption">
                            {backupOptions.map(b => (
                              <MenuItem key={b.id} value={b as any}>{format(b.createdTime || 0, 'Pp')}</MenuItem>
                            ))}
                          </SelectField> :
                          <SelectField label="Encryption Timestamp*" name="encryptionOption">
                            {encryptionOptions.map(b => (
                              <MenuItem key={b.id} value={b as any}>{format(b.createdTime || 0, 'Pp')}</MenuItem>
                            ))}
                          </SelectField>
                        }
                      </Grid>
                      {values.database !== '' && values.database.sizeText &&
                        <Grid item xs={12} md={6} >
                          <ReadonlyField
                            id="dbsize" 
                            label="Database Size" 
                            value={values.database.sizeText.toUpperCase()} 
                          />
                        </Grid>}
                      <Grid item xs={12} md={6} >
                        <ReadonlyField
                          id="backupsize" 
                          label="Backup File Size" 
                          value={values.deployFrom === 'backup'
                            ? (values.backupOption === '' ? '' : values.backupOption.sizeText)
                            : (values.encryptionOption === '' ? '' : values.encryptionOption.sizeText)
                          } 
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ my: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {'Continue'}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                {showNetworkInput &&
                  <Step completed={completion[1]}>
                    <StepButton onClick={handleActivate(1)}>
                      <StepLabel error={stepsError[1]} >
                        {activeStep !== 1 && values.networkConnect !== '' // completion[1] 
                          ? `Network Connection "${(values.networkConnect as NetworkConnectionResponseDto).name}"`
                          : "Choose Network"}
                      </StepLabel>
                    </StepButton>
                    <StepContent>
                      <Grid
                        container
                        spacing={4}
                      >
                        <Grid item xs={12} md={6} >
                          <SelectField label="Network Connectivity" name="networkConnect">
                            {networkConnections.map(c => (
                              <MenuItem key={c.id} value={c as any}>{c.name}</MenuItem>
                            ))}
                          </SelectField>
                        </Grid>
                        <Grid item xs={12} md={6} >
                          <Button 
                            variant="outlined" 
                            color="primary" 
                            startIcon={<AddIcon fontSize="small" />} 
                            onClick={handleAddNewConnection}
                          >
                            Add New
                          </Button>
                        </Grid>
                      </Grid>
                      <Box sx={{ my: 2 }}>
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          {'Continue'}
                        </Button>
                      </Box>
                    </StepContent>
                  </Step>}

                <Step completed={completion[2]}>
                  <StepButton onClick={handleActivate(2 + offset)}>
                    <StepLabel error={stepsError[2]} >
                      {activeStep !== (2 + offset) && completion[2] 
                        ? hostOptionDisplayName(values.hostingOption)
                        : "Hosting Specification"}
                    </StepLabel>
                  </StepButton>
                  <StepContent>
                    <Grid
                      container
                      spacing={4}
                    >
                      <Grid item xs={12} >
                        <RadioGroupField label="Deployment Characteristics" name="deployCharater" row>
                          <FormControlLabel value="production" control={<Radio />} label="Production" />
                          <FormControlLabel value="non-production" control={<Radio />} label="Non-Production" />
                        </RadioGroupField>
                      </Grid>
                      <Grid item xs={12} >
                        <RadioGroupField label="Performance Requirement" name="performanceTier" row>
                          <FormControlLabel value={CustomizedOption.Good + ''} control={<Radio />} label="Good" />
                          <FormControlLabel value={CustomizedOption.Better + ''} control={<Radio />} label="Better" />
                          <FormControlLabel value={CustomizedOption.Best + ''} control={<Radio />} label="Best" />
                        </RadioGroupField>
                      </Grid>
                      <Grid item xs={12} >
                        <SelectField label="Hosting Option*" name="hostingOption">
                          {hostingOptions.map(h => (
                            <MenuItem key={h.id} value={h as any} >{hostOptionDisplayName(h)}</MenuItem>
                          ))}
                        </SelectField>
                      </Grid>
                    </Grid>
                    <Box sx={{ my: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {'Continue'}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                <Step completed={completion[3]}>
                  <StepButton onClick={handleActivate(3 + offset)}>
                    <StepLabel error={stepsError[3]} >
                      {activeStep !== (3 + offset) && completion[3] 
                        ? `Database User "${values.initialDBUser}"`
                        : "Database User Account"}
                    </StepLabel>
                  </StepButton>
                  <StepContent>
                    <Grid
                      container
                      spacing={4}
                    >
                      <Grid item xs={12} md={6} >
                        <TextField label="Database Owner User Name" name="initialDBUser" autoComplete="new-password" />
                      </Grid>
                      <Grid item xs={12} md={6} >
                        <TextField 
                          label="Password" 
                          name="initialDBPass" 
                          type={showPass ? 'text' : 'password'} 
                          autoComplete="new-password" 
                          InputProps={{ endAdornment }}
                        />
                      </Grid>
                    </Grid>
                    <Box sx={{ my: 2 }}>
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {'Continue'}
                      </Button>
                    </Box>
                  </StepContent>
                </Step>

                <Step completed={completion[4]}>
                  <StepButton onClick={handleActivate(4 + offset)}>
                    <StepLabel error={stepsError[4]} >
                      Database Options
                    </StepLabel>
                  </StepButton>
                  <StepContent>
                    <Grid
                      container
                      spacing={4}
                    >
                      {values.database && !values.database.isManaged && 
                        <Grid item xs={12} >
                          <Typography
                            color="textPrimary"
                            gutterBottom
                            variant="subtitle2"
                          >
                            Migrate this Database to OnData?
                          </Typography>
                          <Typography
                            color="textSecondary"
                            variant="body2"
                          >
                            If you choose to migrate to OnData, the backup schedule will be migrated to OnData as well. 
                            No more backups will be accepted from your currently hosted location.
                          </Typography>
                          <Switch
                            checked={values.migrateNeeded}
                            color="primary"
                            edge="start"
                            name="migrateNeeded"
                            onChange={handleChange}
                            value={values.migrateNeeded}
                          />
                        </Grid>}
                      {(values.database && values.database.isManaged || !values.migrateNeeded) &&
                        <>
                          <Grid item xs={12} >
                            <TextField label="Database Name*" name="instanceName" autoComplete="new-password" />
                          </Grid>
                          <Grid item xs={12} >
                            <Typography
                              color="textPrimary"
                              gutterBottom
                              variant="subtitle2"
                            >
                              Clone the existing encryption mappings?
                            </Typography>
                            <Switch
                              checked={values.cloneEncryptionMapping}
                              color="primary"
                              edge="start"
                              name="cloneEncryptionMapping"
                              onChange={handleChange}
                              value={values.cloneEncryptionMapping}
                            />
                          </Grid>
                        </>
                      }
                    </Grid>
                  </StepContent>
                </Step>




              </Stepper>
              
              {errors.submit && (
                <Box sx={{ mt: 3 }}>
                  <FormHelperText error>
                    {errors.submit}
                  </FormHelperText>
                </Box>
              )}
            </CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                p: 2
              }}
            >
              <Button
                color="primary"
                disabled={isSubmitting}
                variant="outlined"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={isSubmitting}
                type="submit"
                variant="contained"
              >
                Deploy Database
              </Button>
            </Box>
          </Card>

        </OdForm>

      </Drawer>
    </>
  )
}

export default DeployDatabase;

const ReadonlyField: FC<TextFieldProps> = (props) => {
  return (
    <MuiTextField
      fullWidth
      variant="outlined"
      InputProps={{
        readOnly: true,
      }}
      {...props}
    />
  )
}

const hostOptionDisplayName = (sku: VmSkuResponseDto | '') =>
  sku !== '' ? `${sku.vcpus} vCPUs, ${sku.ram} GB RAM, ${sku.diskSize}` : ''

